import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "store/authStore";
import useFetch from "hooks/useFetch";
import { useNavigate } from "react-router-dom";
import { saveToken } from "utils";
import { Box, CircularProgress, Typography } from "@mui/material";
import imgSrc from "assets/imageAuth.png";
import CustomInput from "./Input";
import VerificationCodeResendTimer from "./VerificationCodeResendTimer";
import CustomButton from "./Button";
import TelegramIcon from "@mui/icons-material/Telegram";
import CheckIcon from "@mui/icons-material/Check";
import useIsTablet from "hooks/useIsTablet";

interface IVerificationProps {
  onClose: (value: boolean) => void;
}

interface IVerificationFormData {
  code1: string;
  code2: string;
  code3: string;
  code4: string;
  code5: string;
  code6: string;
}

export function Verification({ onClose }: IVerificationProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isTablet = useIsTablet();
  const { unverifiedUserInfo, setUverifiedUserInfo } = useAuthStore();
  const [isCodeInvalid, setIsCodeInvalid] = useState<boolean>(false);
  const [isVerificationSuccess, setIsVerificationSuccess] = useState<boolean>(false);
  const code1Ref = useRef<HTMLInputElement>(null);
  const code2Ref = useRef<HTMLInputElement>(null);
  const code3Ref = useRef<HTMLInputElement>(null);
  const code4Ref = useRef<HTMLInputElement>(null);
  const code5Ref = useRef<HTMLInputElement>(null);
  const code6Ref = useRef<HTMLInputElement>(null);
  const codeNames = ["code1", "code2", "code3", "code4", "code5", "code6"] as const;
  const refs = [code1Ref, code2Ref, code3Ref, code4Ref, code5Ref, code6Ref] as const;

  const [formData, setFormData] = useState<IVerificationFormData>({
    code1: "",
    code2: "",
    code3: "",
    code4: "",
    code5: "",
    code6: "",
  });

  const { loading: isUserVerifyLoading, triggerFetch: userVerify } = useFetch({
    url: "auth/user-verification",
    method: "POST",
    context: { no401Redirect: true },
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const oneDigitValue = value.slice(-1);

    setFormData((prevState) => ({
      ...prevState,
      [name]: oneDigitValue,
    }));

    const index = codeNames.indexOf(name as any);

    if (oneDigitValue) {
      if (index < refs.length - 1) {
        refs[index + 1].current?.focus();
      }
    } else {
      if (index > 0) {
        refs[index - 1].current?.focus();
      }
    }
  };

  const handleUserVerify = async (code: string) => {
    const response = await userVerify({ userId: unverifiedUserInfo.userId, verificationCode: code });

    if (!response.success) {
      setIsCodeInvalid(true);
      return;
    }

    const token = response.data.token;
    saveToken(token);
    setIsCodeInvalid(false);
    setUverifiedUserInfo({ userId: null, verificationMode: null });
    setIsVerificationSuccess(true);

    setTimeout(() => {
      onClose(false);
      navigate("/dashboard");
    }, 2000);
  };

  useEffect(() => {
    const formDataValues = Object.values(formData);

    if (formDataValues.some((value) => value === "")) {
      return;
    }

    const verificationCode = formDataValues.join("");
    void handleUserVerify(verificationCode);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row-reverse" },
        alignItems: "center",
        textAlign: { xs: "center", md: "left" },
        width: { xs: "100%", md: 720 },
        maxWidth: { xs: "auto", md: "720px" },
        margin: "auto",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "center", paddingLeft: "20px", mb: { xs: 2, md: 0 } }}>
        <img src={imgSrc} alt="some-image" style={{ maxWidth: "250px", height: "auto" }} />
      </Box>
      <Box component="form" noValidate sx={{ flex: 1, width: { xs: "100%", md: "auto" } }}>
        <Typography
          variant="subtitle1"
          sx={{
            textAlign: "center",
          }}
        >
          {t("verificationModalTitle")}
        </Typography>
        <Typography variant="subtitle2" sx={{ marginBottom: "24px" }}>
          {unverifiedUserInfo.verificationMode === "telegram"
            ? t("verificationModalTextTelegram")
            : t("verificationModalText")}
        </Typography>

        {unverifiedUserInfo.verificationMode === "telegram" && (
          <Box sx={{ display: "flex", justifyContent: "center", marginBottom: "24px" }}>
            <CustomButton
              type="button"
              width={"max-content"}
              height={40}
              textButton={"Telegram bot"}
              customBgColor="#3390ec"
              customHoverColor="#3390ec"
              customBorderRadius="8px"
              customFontSize="16px"
              customPadding="16px 30px 16px 20px"
              customTextTransform="none"
              startIconSrc={<TelegramIcon sx={{ marginRight: "10px" }} />}
              onClick={() => {
                window.open("https://t.me/ducksay_leads_bot", "_blank", "noopener noreferrer");
              }}
            />
          </Box>
        )}

        <Box sx={{ display: "flex", justifyContent: "center", gap: "10px", marginBottom: "12px" }}>
          {codeNames.map((name, index) => (
            <CustomInput
              sx={{ "input, textarea": { fontWeight: 500, textAlign: "center" } }}
              required
              key={name}
              inputRef={refs[index]}
              name={name}
              value={formData[name]}
              onChange={handleChange}
              autoFocus={index === 0 && !isTablet}
              onlyNumbersValues
              customWidth="40px"
              height={"40px"}
              customFontSize="16px"
              customBgColor="#F6F7F9"
              disabled={isUserVerifyLoading || isVerificationSuccess ? true : false}
            />
          ))}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", height: "30px", marginBottom: "12px" }}>
          {isVerificationSuccess && (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "10px", height: "30px" }}>
              <Typography variant="subtitle2">Success!</Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "30px",
                  height: "30px",
                  borderRadius: "50%",
                  backgroundColor: "#4cd964",
                }}
              >
                <CheckIcon sx={{ color: "#FFFFFF" }} />
              </Box>
            </Box>
          )}
          {isUserVerifyLoading && <CircularProgress size="28px" color="primary" />}
        </Box>

        {isCodeInvalid && (
          <Typography variant="subtitle2" sx={{ marginBottom: "24px", color: "#E02B00" }}>
            {t("verificationCodeError")}
          </Typography>
        )}
        <VerificationCodeResendTimer />
      </Box>
    </Box>
  );
}
